import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { ProgressBar } from '../components/ui/ProgressBar';
import { Button } from '../components/ui/Button';
import { QuizQuestion } from '../components/quiz/QuizQuestion';
import { RadioQuestion } from '../components/quiz/RadioQuestion';
import { MultiSelectQuestion } from '../components/quiz/MultiSelectQuestion';
import { ScaleQuestion } from '../components/quiz/ScaleQuestion';
import { LeadCaptureForm } from '../components/quiz/LeadCaptureForm';
import { ArrowLeftIcon } from 'lucide-react';
import { trackPageView, trackClick } from '../utils/analytics';
interface QuizAnswers {
  age: string | null;
  situation: string | null;
  duration: string | null;
  family: string | null;
  tried: string[];
  stopped: string | null;
  symptoms: string | null;
  commitment: number | null;
  firstName: string;
  email: string;
}
export function QuizPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({
    age: null,
    situation: null,
    duration: null,
    family: null,
    tried: [],
    stopped: null,
    symptoms: null,
    commitment: null,
    firstName: '',
    email: ''
  });
  const totalSteps = 9;
  useEffect(() => {
    trackPageView('quiz');
  }, []);
  const calculateResultType = (): 'early' | 'mid' | 'late' => {
    let earlyVotes = 0;
    let midVotes = 0;
    let lateVotes = 0;
    // Q2: Situation
    if (answers.situation === 'Mostly intact, slight recession at temples') {
      earlyVotes++;
    } else if (answers.situation === 'Thinning at the crown' || answers.situation === 'Both receding hairline and crown thinning') {
      midVotes++;
    } else if (answers.situation === 'Large visible thinning or bald patches') {
      lateVotes++;
    }
    // Q3: Duration
    if (answers.duration === 'Less than 12 months') {
      earlyVotes++;
    } else if (answers.duration === '1–3 years') {
      midVotes++;
    } else if (answers.duration === '3–5 years' || answers.duration === 'More than 5 years') {
      lateVotes++;
    }
    // Q4: Family history
    if (answers.family === 'None / very minimal') {
      earlyVotes++;
    } else if (answers.family === 'Some thinning but not bald' || answers.family === 'One side of family with clear balding') {
      midVotes++;
    } else if (answers.family === 'Both sides with clear balding') {
      lateVotes++;
    }
    // Determine result based on majority
    if (lateVotes > earlyVotes && lateVotes > midVotes) return 'late';
    if (earlyVotes > midVotes && earlyVotes > lateVotes) return 'early';
    return 'mid'; // Default to mid for ties
  };
  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      trackClick('nextQuestion');
      setCurrentStep(currentStep + 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      trackClick('backQuestion');
      setCurrentStep(currentStep - 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const handleLeadCapture = (data: {
    firstName: string;
    email: string;
  }) => {
    trackClick('submitQuiz');
    setAnswers({
      ...answers,
      ...data
    });
    // Log email submission
    const submissionData = {
      timestamp: new Date().toISOString(),
      email: data.email,
      firstName: data.firstName,
      quizAnswers: {
        age: answers.age,
        situation: answers.situation,
        duration: answers.duration,
        family: answers.family,
        tried: answers.tried,
        stopped: answers.stopped,
        symptoms: answers.symptoms,
        commitment: answers.commitment
      }
    };
    // Log to console
    console.log('📧 Email Submission:', submissionData);
    // Store in localStorage
    try {
      const existingSubmissions = localStorage.getItem('crowncontrol_submissions');
      const submissions = existingSubmissions ? JSON.parse(existingSubmissions) : [];
      submissions.push(submissionData);
      localStorage.setItem('crowncontrol_submissions', JSON.stringify(submissions));
      console.log(`✅ Total submissions logged: ${submissions.length}`);
    } catch (error) {
      console.error('Failed to save submission to localStorage:', error);
    }
    const resultType = calculateResultType();
    navigate(`/results?type=${resultType}`);
  };
  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return answers.age !== null;
      case 1:
        return answers.situation !== null;
      case 2:
        return answers.duration !== null;
      case 3:
        return answers.family !== null;
      case 4:
        return answers.tried.length > 0;
      case 5:
        return answers.stopped !== null;
      case 6:
        return answers.symptoms !== null;
      case 7:
        return answers.commitment !== null;
      default:
        return false;
    }
  };
  return <div className="min-h-screen w-full bg-crown-bg flex flex-col" data-id="element-173">
      <Header data-id="element-174" />

      <main className="flex-1 py-8 sm:py-12" data-id="element-175">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-176">
          {/* Progress Bar */}
          {currentStep < 8 && <div className="mb-8 sm:mb-12" data-id="element-177">
              <ProgressBar current={currentStep + 1} total={totalSteps} data-id="element-178" />
            </div>}

          {/* Question Content */}
          <div className="mb-8" data-id="element-179">
            {currentStep === 0 && <QuizQuestion title="Age bracket" data-id="element-180">
                <RadioQuestion name="age" options={['21–27', '28–34', '35–41', '42–50', '50+']} selectedValue={answers.age} onChange={value => setAnswers({
              ...answers,
              age: value
            })} data-id="element-181" />
              </QuizQuestion>}

            {currentStep === 1 && <QuizQuestion title="Which best describes your current hair situation?" data-id="element-182">
                <RadioQuestion name="situation" options={['Mostly intact, slight recession at temples', 'Thinning at the crown', 'Both receding hairline and crown thinning', 'Large visible thinning or bald patches']} selectedValue={answers.situation} onChange={value => setAnswers({
              ...answers,
              situation: value
            })} data-id="element-183" />
              </QuizQuestion>}

            {currentStep === 2 && <QuizQuestion title="How long have you noticed visible thinning?" data-id="element-184">
                <RadioQuestion name="duration" options={['Less than 12 months', '1–3 years', '3–5 years', 'More than 5 years']} selectedValue={answers.duration} onChange={value => setAnswers({
              ...answers,
              duration: value
            })} data-id="element-185" />
              </QuizQuestion>}

            {currentStep === 3 && <QuizQuestion title="Family history of male pattern baldness (close relatives)?" data-id="element-186">
                <RadioQuestion name="family" options={['None / very minimal', 'Some thinning but not bald', 'One side of family with clear balding', 'Both sides with clear balding']} selectedValue={answers.family} onChange={value => setAnswers({
              ...answers,
              family: value
            })} data-id="element-187" />
              </QuizQuestion>}

            {currentStep === 4 && <QuizQuestion title="What have you tried so far?" data-id="element-188">
                <MultiSelectQuestion options={['Nothing yet', 'Minoxidil', 'Finasteride or other oral meds', 'Supplements / shampoos / topicals', 'PRP / microneedling / transplant consult']} selectedValues={answers.tried} onChange={values => setAnswers({
              ...answers,
              tried: values
            })} data-id="element-189" />
              </QuizQuestion>}

            {currentStep === 5 && <QuizQuestion title="Have you stopped any treatment because of side-effects or fear?" data-id="element-190">
                <RadioQuestion name="stopped" options={['Yes, I had side-effects', 'Yes, research scared me off', "No, I'm still using something.",, "I've never started anything."]} selectedValue={answers.stopped} onChange={value => setAnswers({
              ...answers,
              stopped: value
            })} data-id="element-191" />
              </QuizQuestion>}

            {currentStep === 6 && <QuizQuestion title="Which of these do you notice most right now?" data-id="element-192">
                <RadioQuestion name="symptoms" options={['Shedding in the shower or brush', 'Itchy or inflamed scalp', 'Both shedding and itch/inflammation', 'Neither, mostly visual thinning']} selectedValue={answers.symptoms} onChange={value => setAnswers({
              ...answers,
              symptoms: value
            })} data-id="element-193" />
              </QuizQuestion>}

            {currentStep === 7 && <QuizQuestion title="How committed are you to a 10-minute daily or near-daily routine if it's logical and proven?" data-id="element-194">
                <ScaleQuestion selectedValue={answers.commitment} onChange={value => setAnswers({
              ...answers,
              commitment: value
            })} labels={[{
              range: '1–3',
              description: 'Honestly not very'
            }, {
              range: '4–7',
              description: 'If it makes sense, yes'
            }, {
              range: '8–10',
              description: 'I already stick to harder routines'
            }]} data-id="element-195" />
              </QuizQuestion>}

            {currentStep === 8 && <LeadCaptureForm onSubmit={handleLeadCapture} data-id="element-196" />}
          </div>

          {/* Navigation Buttons */}
          {currentStep < 8 && <div className="flex gap-4 justify-between" data-id="element-197">
              <Button variant="secondary" onClick={handleBack} disabled={currentStep === 0} className="flex items-center gap-2" data-id="element-198">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-199" />
                Back
              </Button>

              <Button onClick={handleNext} disabled={!canProceed()} className="flex-1 sm:flex-initial sm:min-w-[200px]" data-id="element-200">
                Next
              </Button>
            </div>}
        </div>
      </main>

      <Footer data-id="element-201" />
    </div>;
}